'use client';

import React, { useContext, useEffect, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import styles from './helpCenterFullView.module.scss';
import { findTopicsByID } from '@/utils/helpCenterUtils';
import ArticlesPanel from './common/articlesPanel';
import TitleNavContainer from './common/titleNavContainer';
import '@chewy/kib-menus-styles/dist/styles/kib-breadcrumbs.css';
import SearchResultsContainer from './common/searchResultsContainer';
import OHCContext from '@/providers/OHCContext';
import PopularTopicsContainer from './common/popularTopicsContainer';
import ExploreMorePanel from './common/exploreMorePanel';
import CustomerWelcomePanel from './common/customerWelcomePanel';

interface HelpCenterFullViewProps {
  id?: string;
}

const HelpCenterFullView: React.FC<HelpCenterFullViewProps> = ({ id }) => {
  const {
    navData,
    searchQuery,
    selectedTopic,
    setSelectedTopic,
    selectedSection,
    setSelectedSection,
    selectedArticle,
    setSelectedArticle,
    exploreChewyLinksData,
    searchResults,
    isResultsLoading,
    isNavError,
    setIsNavError
  } = useContext(OHCContext);

  const isHelpResults = useMemo(() => !isEmpty(searchQuery), [searchQuery]);

  useEffect(() => {
    // Listen for hash changes
    const handleHashChange = () => {
      const topics = findTopicsByID(navData, id);

      setIsNavError(!!topics?.isError);
      setSelectedTopic(topics?.topic);
      setSelectedSection(topics?.section);
      setSelectedArticle(topics?.article);
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    // Cleanup the event listener
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [navData, id, setSelectedTopic, setSelectedSection, setSelectedArticle, setIsNavError]);

  return (
    <div className={styles.root}>
      {isHelpResults || isNavError ? (
        <div className={styles.popularTopics}>
          <div>
            <SearchResultsContainer />
            {(isEmpty(searchResults) || isNavError) && !isResultsLoading && (
              <div className={styles.popularTopicsContainer}>
                <PopularTopicsContainer isRX={false} />
              </div>
            )}
          </div>
          <div className={styles.rightSidebar}>
            <ExploreMorePanel title={'Explore Chewy'} linksData={exploreChewyLinksData} />
          </div>
        </div>
      ) : selectedSection ? (
        <ArticlesPanel articles={selectedSection?.sections} selectedArticle={selectedArticle} isDesktopMode={true} />
      ) : selectedTopic ? (
        <div className={styles.mainContentColumns}>
          <TitleNavContainer dataItems={selectedTopic?.sections} />
        </div>
      ) : (
        <div className={styles.popularTopics}>
          <div>
            <CustomerWelcomePanel />
            <PopularTopicsContainer isRX={false} />
          </div>
          <div className={styles.rightSidebar}>
            <ExploreMorePanel title={'Explore Chewy'} linksData={exploreChewyLinksData} />
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpCenterFullView;
